import { useCallback, useEffect, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Điện thoại (< 640px): một hàng chỉ hiện tối đa `max` nút thao tác, nút kém quan trọng gom vào nút "…".
 * Không dùng cho tabs: tabs cuộn ngang để xem hết.
 * Làm việc trên DOM nên áp dụng được cho mọi trang dùng ListPageShell mà không phải sửa từng trang:
 *  - mục = button / a / [role=tab] / nhóm [role=group] (nút chọn kiểu xem) không nằm trong mục khác;
 *  - mục bị gom được ẩn bằng data-mobile-overflow (CSS chỉ ẩn dưới 640px), bấm trong menu = bấm mục gốc;
 *  - mục mở menu con (aria-haspopup) và nhóm nút luôn hiện, vì không bấm hộ được.
 */
const MOBILE_QUERY = '(max-width: 639px)';
const ITEM_SELECTOR = 'button, a, [role="tab"], [role="group"]';

export type OverflowRank = (el: HTMLElement, index: number) => number;

type OverflowItem = { id: string; label: string };

function itemsOf(container: HTMLElement): HTMLElement[] {
    return (Array.from(container.querySelectorAll(ITEM_SELECTOR)) as HTMLElement[]).filter((el) => {
        const parentItem = el.parentElement?.closest(ITEM_SELECTOR);
        return !(parentItem && container.contains(parentItem)) && !el.closest('[data-mobile-overflow-trigger]');
    });
}

function labelOf(el: HTMLElement): string {
    const text = (el.innerText || '').replace(/\s+/g, ' ').trim();
    return text || el.getAttribute('aria-label') || el.getAttribute('title') || 'Thao tác';
}

const isPinned = (el: HTMLElement) => el.getAttribute('role') === 'group' || el.getAttribute('aria-haspopup') === 'menu' || el.getAttribute('aria-haspopup') === 'true';

/** Nút thao tác: tạo mới / nút tô màu chính trước; xuất, nhập, in, trợ giúp sau */
export const rankAction: OverflowRank = (el) => {
    const text = labelOf(el).toLowerCase();
    if (/^(thêm|tạo|lập|\+)|thêm mới|tạo mới/.test(text)) return 100;
    if (/hướng dẫn|trợ giúp|^\?$|^thao tác$/.test(text) || el.closest('[data-contextual-help]')) return 0;
    if (/xuất|nhập|mẫu|in |^in$|báo cáo|làm mới|tải/.test(text)) return 20;
    const bg = getComputedStyle(el).backgroundColor;
    const filled = bg && !/rgba?\(\s*255,\s*255,\s*255|rgba\(0, 0, 0, 0\)|transparent/.test(bg);
    return filled ? 80 : 50;
};

/**
 * Trả về `ref` (callback ref) gắn vào vùng chứa nút: dùng state thay vì useRef để tự gắn lại khi vùng chứa
 * xuất hiện muộn (VD trang chi tiết chỉ render thanh nút sau khi tải xong dữ liệu).
 */
export function useMobileOverflow(rank: OverflowRank, max = 4) {
    const [box, setBox] = useState<HTMLElement | null>(null);
    const [hidden, setHidden] = useState<OverflowItem[]>([]);

    const recompute = useCallback(() => {
        if (!box) return;
        const all = itemsOf(box);
        all.forEach((el) => el.removeAttribute('data-mobile-overflow'));
        if (!window.matchMedia(MOBILE_QUERY).matches) {
            setHidden((prev) => (prev.length ? [] : prev));
            return;
        }
        const visible = all.filter((el) => el.offsetParent !== null);
        const forcedOverflow = visible.filter((el) => el.hasAttribute('data-toolbar-secondary'));
        const candidates = visible.filter((el) => !el.hasAttribute('data-toolbar-secondary'));
        const pinned = candidates.filter(isPinned);
        const ranked = candidates
            .filter((el) => !isPinned(el))
            .map((el, i) => ({ el, score: rank(el, i), i, w: el.getBoundingClientRect().width }))
            .sort((a, b) => b.score - a.score || a.i - b.i);
        // Tối đa `max` mục mỗi hàng (kể cả nút "…") và phải vừa 1 hàng theo độ rộng thật
        const GAP = 6;
        const TRIGGER = 32 + GAP;
        const avail = (box.closest('.erp-list-toolbar-row') as HTMLElement | null)?.clientWidth || box.clientWidth;
        const width = (els: { w: number }[]) => els.reduce((sum, x) => sum + x.w + GAP, 0);
        const pinnedW = pinned.reduce((sum, el) => sum + el.getBoundingClientRect().width + GAP, 0);
        const hasForced = forcedOverflow.length > 0;
        let kept = ranked.slice(0, Math.max(0, max - 1 - pinned.length));
        if (!hasForced && ranked.length + pinned.length <= max && pinnedW + width(ranked) <= avail - 24) kept = ranked;
        while (kept.length > 1 && pinnedW + width(kept) + (hasForced || kept.length < ranked.length ? TRIGGER : 0) > avail - 24) kept = kept.slice(0, -1);
        if (!hasForced && kept.length === ranked.length) {
            setHidden((prev) => (prev.length ? [] : prev));
            return;
        }
        const keep = new Set<HTMLElement>([...pinned, ...kept.map((x) => x.el)]);
        const next: OverflowItem[] = [];
        visible.forEach((el, i) => {
            if (keep.has(el)) return;
            const id = `ov-${i}`;
            el.setAttribute('data-mobile-overflow', id);
            next.push({ id, label: labelOf(el) });
        });
        setHidden((prev) => (prev.length === next.length && prev.every((p, k) => p.id === next[k].id && p.label === next[k].label) ? prev : next));
    }, [box, rank, max]);

    useEffect(() => {
        if (!box) return;
        let raf = 0;
        const schedule = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(recompute);
        };
        schedule();
        const mo = new MutationObserver(schedule);
        mo.observe(box, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-selected', 'data-state', 'aria-current', 'class'] });
        const mq = window.matchMedia(MOBILE_QUERY);
        mq.addEventListener('change', schedule);
        return () => {
            cancelAnimationFrame(raf);
            mo.disconnect();
            mq.removeEventListener('change', schedule);
        };
    }, [box, recompute]);

    const activate = useCallback((id: string) => {
        const el = box?.querySelector(`[data-mobile-overflow="${id}"]`) as HTMLElement | null;
        el?.click();
    }, [box]);

    return { ref: setBox, hidden, activate };
}

export function MobileOverflowMenu({ items, onSelect, label = 'Thêm thao tác', className }: {
    items: OverflowItem[];
    onSelect: (id: string) => void;
    label?: string;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!items.length) setOpen(false);
    }, [items.length]);
    if (!items.length) return null;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-mobile-overflow-trigger
                    data-testid="mobile-overflow-trigger"
                    aria-label={label}
                    title={label}
                    className={cn('sm:hidden inline-flex items-center justify-center h-7 w-8 shrink-0 rounded-md border border-slate-200 bg-white text-slate-600 active:bg-slate-100', className)}
                >
                    <MoreHorizontal className="h-4 w-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1" onCloseAutoFocus={(e) => e.preventDefault()}>
                {items.map((it) => (
                    <button
                        key={it.id}
                        type="button"
                        data-testid="mobile-overflow-item"
                        className="w-full text-left px-2.5 py-2 text-sm rounded-md hover:bg-slate-100 active:bg-slate-100"
                        onClick={() => {
                            setOpen(false);
                            window.requestAnimationFrame(() => onSelect(it.id));
                        }}
                    >
                        {it.label}
                    </button>
                ))}
            </PopoverContent>
        </Popover>
    );
}
